import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

const LEG_SWING = 0.55   // radians max leg rotation
const ARM_SWING = 0.45   // radians max arm rotation
const TORSO_BOB = 0.012  // vertical bob amount
const BLEND_SPEED = 8    // how fast walk blends in/out

// Colors — medieval sailor/pirate palette
const SKIN = '#d4a574'
const HAT = '#2a1c10'
const SHIRT = '#c8b89a'
const BELT_COLOR = '#3a2a18'
const PANTS = '#4a3820'
const BOOTS = '#2a1c10'

interface CharacterModelProps {
  walkPhase: React.MutableRefObject<number>
  isMoving: React.MutableRefObject<boolean>
}

export function CharacterModel({ walkPhase, isMoving }: CharacterModelProps) {
  const leftArmRef = useRef<THREE.Group>(null)
  const rightArmRef = useRef<THREE.Group>(null)
  const leftLegRef = useRef<THREE.Group>(null)
  const rightLegRef = useRef<THREE.Group>(null)
  const torsoRef = useRef<THREE.Mesh>(null)
  const headRef = useRef<THREE.Group>(null)
  const swingAmp = useRef(0)

  useFrame((_, delta) => {
    const dt = Math.min(delta, 0.1)

    // Smooth blend between walking and standing
    const target = isMoving.current ? 1 : 0
    swingAmp.current += (target - swingAmp.current) * Math.min(1, dt * BLEND_SPEED)

    const amp = swingAmp.current
    const phase = walkPhase.current
    const swing = Math.sin(phase)

    // Model faces -Z, so positive X rotation swings leg bottom toward +Z (backward).
    // Negate so forward motion matches forward leg swing.
    if (leftLegRef.current) leftLegRef.current.rotation.x = -swing * LEG_SWING * amp
    if (rightLegRef.current) rightLegRef.current.rotation.x = swing * LEG_SWING * amp

    // Arms swing opposite to legs
    if (leftArmRef.current) leftArmRef.current.rotation.x = swing * ARM_SWING * amp
    if (rightArmRef.current) rightArmRef.current.rotation.x = -swing * ARM_SWING * amp

    // Subtle torso bob (double frequency — bob peaks at each step)
    if (torsoRef.current) {
      torsoRef.current.position.y = 1.15 + Math.abs(swing) * TORSO_BOB * amp
    }

    // Subtle head bob (less than torso)
    if (headRef.current) {
      headRef.current.position.y = 1.62 + Math.abs(swing) * TORSO_BOB * 0.5 * amp
    }
  })

  // Character faces -Z (THREE.js lookAt convention: -Z = forward)
  return (
    <>
      {/* ── Head ── */}
      <group ref={headRef} position={[0, 1.62, 0]}>
        <mesh>
          <sphereGeometry args={[0.12, 8, 6]} />
          <meshLambertMaterial color={SKIN} />
        </mesh>
        {/* Eyes — face -Z (forward) */}
        <mesh position={[-0.04, 0.02, -0.11]}>
          <sphereGeometry args={[0.015, 4, 4]} />
          <meshLambertMaterial color="#1a1008" />
        </mesh>
        <mesh position={[0.04, 0.02, -0.11]}>
          <sphereGeometry args={[0.015, 4, 4]} />
          <meshLambertMaterial color="#1a1008" />
        </mesh>
      </group>

      {/* ── Hat (tricorn-style) ── */}
      <mesh position={[0, 1.74, 0]}>
        <boxGeometry args={[0.28, 0.02, 0.24]} />
        <meshLambertMaterial color={HAT} />
      </mesh>
      <mesh position={[0, 1.80, 0]}>
        <boxGeometry args={[0.16, 0.09, 0.14]} />
        <meshLambertMaterial color={HAT} />
      </mesh>

      {/* ── Torso (shirt) ── */}
      <mesh ref={torsoRef} position={[0, 1.15, 0]}>
        <boxGeometry args={[0.34, 0.50, 0.18]} />
        <meshLambertMaterial color={SHIRT} />
      </mesh>

      {/* ── Belt ── */}
      <mesh position={[0, 0.89, 0]}>
        <boxGeometry args={[0.35, 0.05, 0.19]} />
        <meshLambertMaterial color={BELT_COLOR} />
      </mesh>

      {/* ── Left Arm (pivot at shoulder) ── */}
      <group ref={leftArmRef} position={[-0.22, 1.35, 0]}>
        {/* Upper arm (shirtsleeve) */}
        <mesh position={[0, -0.10, 0]}>
          <boxGeometry args={[0.09, 0.20, 0.09]} />
          <meshLambertMaterial color={SHIRT} />
        </mesh>
        {/* Forearm (skin) */}
        <mesh position={[0, -0.26, 0]}>
          <boxGeometry args={[0.08, 0.14, 0.08]} />
          <meshLambertMaterial color={SKIN} />
        </mesh>
        {/* Hand */}
        <mesh position={[0, -0.36, 0]}>
          <boxGeometry args={[0.06, 0.06, 0.06]} />
          <meshLambertMaterial color={SKIN} />
        </mesh>
      </group>

      {/* ── Right Arm (pivot at shoulder) ── */}
      <group ref={rightArmRef} position={[0.22, 1.35, 0]}>
        <mesh position={[0, -0.10, 0]}>
          <boxGeometry args={[0.09, 0.20, 0.09]} />
          <meshLambertMaterial color={SHIRT} />
        </mesh>
        <mesh position={[0, -0.26, 0]}>
          <boxGeometry args={[0.08, 0.14, 0.08]} />
          <meshLambertMaterial color={SKIN} />
        </mesh>
        <mesh position={[0, -0.36, 0]}>
          <boxGeometry args={[0.06, 0.06, 0.06]} />
          <meshLambertMaterial color={SKIN} />
        </mesh>
      </group>

      {/* ── Left Leg (pivot at hip) ── */}
      <group ref={leftLegRef} position={[-0.09, 0.86, 0]}>
        {/* Thigh + shin (pants) */}
        <mesh position={[0, -0.22, 0]}>
          <boxGeometry args={[0.12, 0.42, 0.12]} />
          <meshLambertMaterial color={PANTS} />
        </mesh>
        {/* Boot — toe points -Z (forward) */}
        <mesh position={[0, -0.46, -0.01]}>
          <boxGeometry args={[0.11, 0.06, 0.15]} />
          <meshLambertMaterial color={BOOTS} />
        </mesh>
      </group>

      {/* ── Right Leg (pivot at hip) ── */}
      <group ref={rightLegRef} position={[0.09, 0.86, 0]}>
        <mesh position={[0, -0.22, 0]}>
          <boxGeometry args={[0.12, 0.42, 0.12]} />
          <meshLambertMaterial color={PANTS} />
        </mesh>
        <mesh position={[0, -0.46, -0.01]}>
          <boxGeometry args={[0.11, 0.06, 0.15]} />
          <meshLambertMaterial color={BOOTS} />
        </mesh>
      </group>
    </>
  )
}
